import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import CalendarComponent, { formatDate } from "../calendar_component/calendar_component";

const ShiftLogCalendarDisplayForSingleDate = ({
  nurse_recap,
  shift_log,
  shift_log_count,
  nurse_recap_count,
  not_seen_count,
  isPeer,
  isPartner
}) => {
  const canViewNurseRecap = !isPeer && !isPartner;
  const hasVisibleRecords = shift_log_count > 0 || (canViewNurseRecap && nurse_recap_count > 0);

  if (!hasVisibleRecords) {
    return (
      <div className="ccw-shift-log-day-actions">
        <a className="ccw-shift-log-day-action ccw-shift-log-day-action--empty" href={shift_log}>
          No records
        </a>
      </div>
    );
  }

  return (
    <div className="ccw-shift-log-day-actions">
      {shift_log_count > 0 && (
        <a className="ccw-shift-log-day-action" href={shift_log}>
          <span>Shift Logs</span>
          <span className="ccw-shift-log-day-count" aria-label={`${shift_log_count} Shift Logs`}>
            {shift_log_count}
          </span>
        </a>
      )}
      {canViewNurseRecap && nurse_recap && nurse_recap_count > 0 && (
        <a className="ccw-shift-log-day-action" href={nurse_recap}>
          <span>Nurse Recap</span>
          <span
            className="ccw-shift-log-day-count"
            aria-label={`${nurse_recap_count} Nurse Recaps`}
          >
            {nurse_recap_count}
          </span>
        </a>
      )}
      {not_seen_count > 0 && (
        <span className="ccw-shift-log-day-flag">{not_seen_count} not seen</span>
      )}
    </div>
  );
};

ShiftLogCalendarDisplayForSingleDate.propTypes = {
  nurse_recap: PropTypes.string,
  shift_log: PropTypes.string.isRequired,
  shift_log_count: PropTypes.number,
  nurse_recap_count: PropTypes.number,
  not_seen_count: PropTypes.number,
  isPeer: PropTypes.bool,
  isPartner: PropTypes.bool
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const normalizeMonth = (month) => {
  if (typeof month === "number") {
    return month;
  }

  if (!month) {
    return 0;
  }

  const parsedMonth = parseInt(month, 10);
  if (!Number.isNaN(parsedMonth)) {
    return Math.min(Math.max(parsedMonth - 1, 0), 11);
  }

  const monthIndex = monthNames.findIndex((name) => name.toLowerCase() === month.toLowerCase());
  return monthIndex === -1 ? 0 : monthIndex;
};

const normalizeYear = (year) => {
  if (typeof year === "number") {
    return year;
  }

  const parsedYear = parseInt(year, 10);
  return Number.isNaN(parsedYear) ? new Date().getFullYear() : parsedYear;
};

const parseCalendarDate = (date) => {
  if (date instanceof Date) {
    return date;
  }

  if (typeof date === "string") {
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  return new Date(date);
};

const normalizeDateKey = (date) => {
  if (!date) {
    return null;
  }

  const parsedDate = parseCalendarDate(date);
  return Number.isNaN(parsedDate.getTime()) ? `${date}` : formatDate(parsedDate);
};

const ShiftLogCalendar = ({ month, year, dates, isPeer, isPartner }) => {
  const eventsByDate = useMemo(() => {
    const map = {};
    (dates || []).forEach(
      ({ date, nurse_recap, shift_log, shift_log_count, nurse_recap_count, not_seen_count }) => {
        const dateKey = normalizeDateKey(date);
        if (dateKey) {
          map[dateKey] = (
            <ShiftLogCalendarDisplayForSingleDate
              nurse_recap={nurse_recap}
              shift_log={shift_log}
              shift_log_count={shift_log_count || 0}
              nurse_recap_count={nurse_recap_count || 0}
              not_seen_count={not_seen_count || 0}
              isPeer={isPeer}
              isPartner={isPartner}
            />
          );
        }
      }
    );
    return map;
  }, [dates, isPeer, isPartner]);

  const displayForDatesFunc = useCallback(
    (day) => {
      const dateKey = formatDate(day);
      return eventsByDate[dateKey] || null;
    },
    [eventsByDate]
  );

  return (
    <CalendarComponent
      displayForDatesFunc={displayForDatesFunc}
      month={normalizeMonth(month)}
      year={normalizeYear(year)}
      useShiftDate
    />
  );
};

ShiftLogCalendar.propTypes = {
  month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
      nurse_recap: PropTypes.string,
      shift_log: PropTypes.string.isRequired,
      shift_log_count: PropTypes.number,
      nurse_recap_count: PropTypes.number,
      not_seen_count: PropTypes.number
    })
  ),
  isPeer: PropTypes.bool,
  isPartner: PropTypes.bool
};

ShiftLogCalendar.defaultProps = {
  isPeer: false,
  isPartner: false
};

export default ShiftLogCalendar;
